import React from 'react';
import { Box, Typography, Chip, Skeleton } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

// Sonido de máquina de escribir usando Web Audio API
const naraKeystroke = (() => {
  let ctx = null;
  return () => {
    try {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      const t = ctx.currentTime;

      const bufferSize = ctx.sampleRate * 0.025;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.15));
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;

      const bandpass = ctx.createBiquadFilter();
      bandpass.type = 'bandpass';
      bandpass.frequency.value = 3000 + Math.random() * 1500;
      bandpass.Q.value = 1.5;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.08 + Math.random() * 0.03, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.03);

      noise.connect(bandpass);
      bandpass.connect(gain);
      gain.connect(ctx.destination);
      noise.start(t);
      noise.stop(t + 0.03);
    } catch (e) {}
  };
})();

const monoStyle = {
  fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "Courier New", monospace',
};

// Componente de typewriter para briefing de paciente (resumen + alertas + meta)
const NaraTypewriter = ({ briefing }) => {
  const [displayedText, setDisplayedText] = React.useState('');
  const [showAlerts, setShowAlerts] = React.useState(false);
  const [showMeta, setShowMeta] = React.useState(false);
  const fullText = briefing.resumen || '';

  React.useEffect(() => {
    setDisplayedText('');
    setShowAlerts(false);
    setShowMeta(false);
    if (!fullText) return;

    let i = 0;
    const speed = 35;
    const timer = setInterval(() => {
      i++;
      setDisplayedText(fullText.slice(0, i));
      const ch = fullText[i - 1];
      if (ch && ch !== ' ') naraKeystroke();
      if (i >= fullText.length) {
        clearInterval(timer);
        setTimeout(() => setShowAlerts(true), 200);
        setTimeout(() => setShowMeta(true), 400);
      }
    }, speed);
    return () => clearInterval(timer);
  }, [fullText]);

  return (
    <Box>
      <Typography sx={{
        ...monoStyle,
        fontSize: 11, color: '#e2e8f0', lineHeight: 1.7, whiteSpace: 'pre-wrap'
      }}>
        <Box component="span" sx={{ color: '#10b981', fontWeight: 700 }}>{'>'} </Box>
        {displayedText}
        {displayedText.length < fullText.length && (
          <Box component="span" sx={{
            '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
            animation: 'blink 0.8s step-end infinite',
            color: '#10b981', fontWeight: 700
          }}>|</Box>
        )}
      </Typography>

      {showAlerts && briefing.alertas && briefing.alertas.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap',
          '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
          animation: 'fadeIn 0.3s ease-out'
        }}>
          {briefing.alertas.map((alerta, idx) => (
            <Chip key={idx} label={alerta} size="small"
              icon={<WarningIcon sx={{ fontSize: '12px !important' }} />}
              sx={{
                ...monoStyle, fontSize: 9.5, fontWeight: 600, height: 20,
                bgcolor: 'rgba(245, 158, 11, 0.15)',
                color: '#fbbf24',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                '& .MuiChip-icon': { color: '#f59e0b' }
              }}
            />
          ))}
        </Box>
      )}

      {showMeta && (briefing.proxima_cita || briefing.ultimo_contacto_whatsapp) && (
        <Box sx={{
          mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)',
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          animation: 'fadeIn 0.3s ease-out'
        }}>
          {briefing.proxima_cita && (
            <Typography sx={{ ...monoStyle, fontSize: 10, color: '#64748b', mb: 0.3 }}>
              <Box component="span" sx={{ color: '#0ea5e9' }}>cita</Box>
              <Box component="span" sx={{ color: '#475569' }}> :: </Box>
              {briefing.proxima_cita}
            </Typography>
          )}
          {briefing.ultimo_contacto_whatsapp && (
            <Typography sx={{ ...monoStyle, fontSize: 10, color: '#64748b' }}>
              <Box component="span" sx={{ color: '#25D366' }}>whatsapp</Box>
              <Box component="span" sx={{ color: '#475569' }}> :: </Box>
              {briefing.ultimo_contacto_whatsapp.substring(0, 80)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// Componente genérico de typewriter para texto simple (morning briefing, post-consulta)
const NaraSimpleTypewriter = ({ text, onComplete }) => {
  const [displayedText, setDisplayedText] = React.useState('');
  const onCompleteRef = React.useRef(onComplete);
  onCompleteRef.current = onComplete;

  React.useEffect(() => {
    setDisplayedText('');
    if (!text) return;

    let i = 0;
    const speed = 35;
    const timer = setInterval(() => {
      i++;
      setDisplayedText(text.slice(0, i));
      const ch = text[i - 1];
      if (ch && ch !== ' ') naraKeystroke();
      if (i >= text.length) {
        clearInterval(timer);
        if (onCompleteRef.current) setTimeout(onCompleteRef.current, 200);
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text]);

  return (
    <Typography sx={{
      ...monoStyle,
      fontSize: 11, color: '#e2e8f0', lineHeight: 1.7, whiteSpace: 'pre-wrap'
    }}>
      <Box component="span" sx={{ color: '#10b981', fontWeight: 700 }}>{'>'} </Box>
      {displayedText}
      {displayedText.length < (text || '').length && (
        <Box component="span" sx={{
          '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
          animation: 'blink 0.8s step-end infinite',
          color: '#10b981', fontWeight: 700
        }}>|</Box>
      )}
    </Typography>
  );
};

// Contenedor de terminal oscuro
const NaraTerminalCard = ({ label, loading, loadingText, children, width, sx }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, ...sx }}>
    <Box sx={{
      width: width || { xs: '100%', sm: 520 }, maxWidth: '100%',
      bgcolor: '#0f172a',
      borderRadius: 2.5,
      p: 2,
      border: '1px solid rgba(16, 185, 129, 0.2)',
      boxShadow: '0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""', position: 'absolute',
        top: 0, left: 0, right: 0, height: 2,
        background: 'linear-gradient(90deg, #10b981 0%, #059669 50%, transparent 100%)'
      }
    }}>
      {/* Terminal header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5, mr: 1.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b' }} />
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
        </Box>
        <Typography sx={{
          ...monoStyle,
          fontSize: 10, color: '#64748b', letterSpacing: '0.05em'
        }}>
          {label || 'nara@system'}
        </Typography>
      </Box>

      {loading ? (
        <Box>
          <Typography sx={{
            ...monoStyle,
            fontSize: 11.5, color: '#10b981', lineHeight: 1.8
          }}>
            <Box component="span" sx={{ color: '#059669' }}>{'>'} </Box>
            {loadingText || 'Analizando'}
            <Box component="span" sx={{
              '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
              animation: 'blink 1s step-end infinite',
              color: '#10b981', ml: 0.3
            }}>...</Box>
          </Typography>
        </Box>
      ) : children}
    </Box>
  </Box>
);

export { naraKeystroke, NaraTypewriter, NaraSimpleTypewriter, NaraTerminalCard, monoStyle };
